var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { makeAutoObservable, runInAction } from 'mobx';
import i18n from 'i18next';
import _ from 'lodash';
import { notesStateService } from 'ziphy-web-shared/basic/api';
import { getEncounterStatus } from 'ziphy-web-shared/basic/entities/encounters';
import { $definitions } from 'ziphy-web-shared/basic/entities/notes/definitions';
import { getUuid } from 'ziphy-web-shared/basic/helpers';
import { $loader } from 'ziphy-web-shared/basic/utils';
import { BlockFactory } from './block/Block.store';
import { getErrorPath } from './Notes.helpers';
import { SectionStore } from './section/Section.store';
const getEmptyTextBlockData = (ref) => ({
    id: getUuid(),
    ref: ref,
    type: 'text',
    source: 'manual',
    title: null,
    blocked: false,
    touched: false,
    text: '',
    data: {},
});
export class NotesStore {
    constructor() {
        this.practice = null;
        this.appointment = null;
        this.encounter = null;
        this.patient = null;
        this.isStartProvider = null;
        this.sidebar = null;
        this.layout = null;
        this.openError = false;
        this._availableLayouts = [];
        this._sections = new Map();
        this._blocks = new Map();
        this._blocksData = new Map();
        this.signature = null;
        this.addendum = [];
        /*
         * State Control
         */
        this.isPdf = false;
        this.isLocked = false;
        this.isSubmitted = false;
        this.errors = {
            empty_section: new Set(),
            unspecified_code: new Set(),
            empty_code: new Set(),
            empty_placeholder: new Set(),
            empty_template: new Set(),
        };
        makeAutoObservable(this);
    }
    clear(full) {
        if (full) {
            this.practice = null;
            this.appointment = null;
            this.encounter = null;
            this.patient = null;
            this.isStartProvider = null;
            this.openError = false;
        }
        // this.sidebar = null
        this.layout = null;
        // this._availableLayouts = []
        this._sections.clear();
        this._blocksData.clear();
        this._blocks.clear();
        this.signature = null;
        this.addendum = [];
        this.clearState(full);
    }
    // Computed
    get availableLayouts() {
        const result = this._availableLayouts.map((x) => ({ id: x.id, text: x.name }));
        if (!result.find((x) => x.id === this.layout.layoutId)) {
            result.unshift({ id: this.layout.layoutId, text: this.layout.name || this.layout.layoutId });
        }
        return result;
    }
    get sections() {
        return Array.from(this._sections.values());
    }
    get practiceSettings() {
        return {
            specifiedConditions: this.practice.specifiedConditions,
            billingReasonLimit: this.practice.billingReasonLimit,
            cptSearchLimit: this.practice.cptSearchLimit,
        };
    }
    get uniqueBlocksData() {
        return new Map([...this._blocksData].filter(([key, value]) => {
            return $definitions.macros.items[key];
        }));
    }
    // Mutations
    UPDATE_CONTEXT({ practice, encounter, appointment, patient, isStartProvider, }) {
        if (practice) {
            this.practice = this.practice ? Object.assign(Object.assign({}, this.practice), practice) : practice;
        }
        if (encounter) {
            this.encounter = this.encounter ? Object.assign(Object.assign({}, this.encounter), encounter) : encounter;
        }
        if (appointment) {
            this.appointment = this.appointment ? Object.assign(Object.assign({}, this.appointment), appointment) : appointment;
        }
        if (patient) {
            this.patient = this.patient ? Object.assign(Object.assign({}, this.patient), patient) : patient;
        }
        this.isStartProvider = isStartProvider;
    }
    UPDATE_STATE(data, options) {
        if (!data)
            return;
        const initiatorRef = data.insertedRef || (options === null || options === void 0 ? void 0 : options.initialRef);
        this.isLocked = data.locked;
        this.layout = data.layout;
        data.blocks.forEach((x) => {
            if (options === null || options === void 0 ? void 0 : options.emptyBlockRef) {
                const blockData = this._blocksData.get(options.emptyBlockRef);
                const blockId = blockData.id;
                this._blocksData.set(options.emptyBlockRef, getEmptyTextBlockData(options.emptyBlockRef));
                this._blocksData.set(x.ref, Object.assign(Object.assign({}, x), { id: blockId }));
            }
            else {
                const oldBlockData = this._blocksData.get(x.ref);
                if (oldBlockData && oldBlockData.id) {
                    this._blocksData.set(x.ref, Object.assign(Object.assign({}, x), { id: oldBlockData.id }));
                }
                else {
                    this._blocksData.set(x.ref, x);
                }
            }
            if (initiatorRef && initiatorRef !== x.ref) {
                this.highlightBlock(x.ref, 'new');
            }
        });
        this.signature = data.signature;
        this.addendum = data.addendum;
        this.layout.sections.forEach((section) => {
            if (!this._sections.has(section.sectionId)) {
                this._sections.set(section.sectionId, new SectionStore({ parent: this }));
            }
            this.getSection(section.sectionId).UPDATE_DATA(section);
        });
        this.REMOVE_UNUSED_BLOCKS();
        if (this.isSubmitted) {
            setTimeout(() => this.validate(), 0);
        }
    }
    UPDATE_BLOCK_DATA(ref, data) {
        const block = this._blocksData.get(ref);
        if (block) {
            this._blocksData.set(ref, _.merge(block, data));
        }
    }
    SET_SIDEBAR(value) {
        this.sidebar = value;
    }
    REMOVE_UNUSED_BLOCKS() {
        const existingRefs = new Set(this.sections.flatMap((section) => {
            return Array.from(section.allFlattenBlocksMap.keys());
        }));
        this._blocks.forEach((block, key) => {
            if (existingRefs.has(block.ref))
                return;
            this._blocksData.delete(block.ref);
            this._blocks.delete(key);
        });
    }
    // Actions
    getSection(id) {
        return this._sections.get(id);
    }
    getBlock(sectionId, ref) {
        const key = `${sectionId}_${ref}`;
        if (!this._blocks.has(key)) {
            runInAction(() => this._blocks.set(key, BlockFactory.create(this, sectionId, ref)));
        }
        return this._blocks.get(key);
    }
    getEmptyBlock(sectionId, parentRef, deep = 0) {
        const key = `${sectionId}_${parentRef}_empty_text_${deep}`;
        if (!this._blocks.has(key)) {
            runInAction(() => {
                if (!this._blocksData.has(key)) {
                    this._blocksData.set(key, getEmptyTextBlockData(key));
                }
                this._blocks.set(key, BlockFactory.createEmptyText(this, sectionId, key));
            });
        }
        return this._blocks.get(key);
    }
    highlightBlock(ref, type) {
        setTimeout(() => $loader.simpleTrigger(`highlight_block_${ref}`, { type }));
    }
    open({ appointment, practice, encounter, patient, isStartProvider, provenanceRef, }) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            if (!(encounter === null || encounter === void 0 ? void 0 : encounter.id) ||
                (encounter.id === ((_a = this.encounter) === null || _a === void 0 ? void 0 : _a.id) &&
                    (encounter === null || encounter === void 0 ? void 0 : encounter.startTime) === ((_b = this.encounter) === null || _b === void 0 ? void 0 : _b.startTime) &&
                    (encounter === null || encounter === void 0 ? void 0 : encounter.cancelTime) === ((_c = this.encounter) === null || _c === void 0 ? void 0 : _c.cancelTime) &&
                    (encounter === null || encounter === void 0 ? void 0 : encounter.finishTime) === ((_d = this.encounter) === null || _d === void 0 ? void 0 : _d.finishTime)))
                return;
            if (provenanceRef)
                this.isPdf = true;
            const [response, layoutsResponse] = yield Promise.all([
                this.isPdf
                    ? notesStateService.openSigned({ encounterId: encounter.id, provenanceRef })
                    : notesStateService.open({ encounterId: encounter.id }),
                notesStateService.availableLayouts({ encounterId: encounter.id }),
                $definitions.init(),
            ]);
            runInAction(() => {
                var _a;
                this.clear(true);
                if (!response.error) {
                    this.UPDATE_CONTEXT({ practice, appointment, encounter, patient, isStartProvider });
                    this.UPDATE_STATE(response.prepared);
                    if (!layoutsResponse.error) {
                        this._availableLayouts = ((_a = layoutsResponse.prepared) === null || _a === void 0 ? void 0 : _a.items) || [];
                    }
                }
                else {
                    this.openError = true;
                }
            });
            return response;
        });
    }
    refresh(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield notesStateService.refresh(this.getParams(params));
            if (!response.error)
                runInAction(() => this.UPDATE_STATE(response.prepared));
            return response;
        });
    }
    switchLayout(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield notesStateService.switchLayout(this.getParams(params));
            if (!response.error) {
                runInAction(() => {
                    this.clear(false);
                    this.UPDATE_STATE(response.prepared);
                });
            }
            return response;
        });
    }
    runMacro(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield notesStateService.runMacro(this.getParams(params));
            if (!response.error)
                runInAction(() => this.UPDATE_STATE(response.prepared));
            return response;
        });
    }
    insertBlock(params, ref) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield notesStateService.insertBlock(this.getParams(params));
            if (!response.error) {
                runInAction(() => this.UPDATE_STATE(response.prepared, { emptyBlockRef: ref }));
            }
            return response;
        });
    }
    clearState(full) {
        if (full) {
            this.clearErrors();
            this.isLocked = false;
            this.isSubmitted = false;
        }
    }
    clearErrors() {
        Object.values(this.errors).forEach((x) => x.clear());
    }
    // Computed
    get encounterStatus() {
        return getEncounterStatus(this.encounter);
    }
    get isEditable() {
        return !this.isLocked && this.encounterStatus.isStarted && !this.encounterStatus.isCancelled;
    }
    get isPlain() {
        return !this.isEditable || this.isPdf;
    }
    get isSigned() {
        var _a;
        return Boolean((_a = this.signature) === null || _a === void 0 ? void 0 : _a.author);
    }
    get isCoSigned() {
        var _a;
        return Boolean((_a = this.signature) === null || _a === void 0 ? void 0 : _a.coauthor);
    }
    get isChanged() {
        var _a;
        return Boolean((_a = this.signature) === null || _a === void 0 ? void 0 : _a.isChanged);
    }
    get beforeSignErrors() {
        let result = new Set();
        if (this.isLocked)
            result.add('is_locked');
        if (!this.isStartProvider && !this.encounterStatus.isFinished)
            result.add('can_not_signing');
        if (this.encounterStatus.isCancelled)
            result.add('encounter_cancelled');
        if (!this.encounterStatus.isStarted)
            result.add('encounter_not_started');
        if (this.encounterStatus.isInProgress)
            result.add('encounter_not_finished');
        return result;
    }
    get validateErrors() {
        let result = [];
        if (this.errors.unspecified_code.size) {
            result.push({
                code: 'unspecified_code',
                text: i18n.t('alert.notes_specify_unspecified_code'),
                refs: Array.from(this.errors.unspecified_code.values()),
            });
        }
        if (this.errors.empty_code.size) {
            result.push({
                code: 'empty_code',
                text: i18n.t('alert.notes_specify_code'),
                refs: Array.from(this.errors.empty_code.values()),
            });
        }
        if (this.errors.empty_placeholder.size) {
            const refs = Array.from(this.errors.empty_placeholder.values());
            result.push({
                code: 'empty_placeholder',
                text: i18n.t('alert.notes_fill_placeholders'),
                refs,
            });
        }
        if (this.errors.empty_template.size) {
            const refs = Array.from(this.errors.empty_template.values());
            result.push({
                code: 'empty_template',
                text: i18n.t('alert.notes_fill_templates'),
                refs,
            });
        }
        if (this.errors.empty_section.size) {
            const refs = Array.from(this.errors.empty_section.values());
            result.push({
                code: 'empty_section',
                text: i18n.t('alert.notes_not_filled_in', {
                    sectionNames: refs.map((x) => this.getSection(x).title).join(', '),
                }),
                refs,
            });
        }
        return result;
    }
    // Mutations
    SET_ERROR(error, item, action = true) {
        const str = getErrorPath(item.sectionId, item.blockRef, item.ref);
        if (action) {
            this.errors[error].add(str);
        }
        else {
            this.errors[error].delete(str);
        }
    }
    SET_IS_SUBMITTED(value) {
        this.isSubmitted = value;
    }
    // Actions
    validate() {
        this.clearErrors();
        this.sections.forEach((x) => x.validate());
    }
    sign(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield notesStateService.sign(this.getParams(params));
            if (!response.error) {
                runInAction(() => {
                    this.clear(false);
                    this.UPDATE_STATE(response.prepared);
                });
            }
            return response;
        });
    }
    /*
     * Other
     */
    getParams(params = {}) {
        return Object.assign(Object.assign({}, params), { encounterId: this.encounter.id });
    }
}
export const $notes = new NotesStore();
/*
 * Other
 */
// $loader.subscribe(globalLoaderHandlers.LOGOUT, () => {
// })
